import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LifeBuoy, MapPin, AlertTriangle, Trash2, ListChecks, ClipboardCheck, FileStack, CloudDownload, Compass, Pencil, Ticket, Printer, Move, Info, SlidersHorizontal, UserCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useTour } from "@/context/TourContext";

const TOPICS = [
  { id: "projekt", icon: Pencil, title: "Projekt anlegen und bearbeiten", steps: [
    "Unter „Projekte“ mit „Neues Projekt“ eine Einrichtung anlegen.",
    "Zum Ändern auf der Projektkarte den Stift antippen oder im geöffneten Projekt „Bearbeiten“ wählen.",
    "Adresse, Bauherr, Auftragnehmer, Architekt, Bauleiter, Status, Termine und Beschreibung anpassen – mit „anzeigen“ legen Sie fest, welche Felder sichtbar sind.",
    "„Änderungen speichern“ – die Daten erscheinen sofort auf Karte, Projektseite und in den Berichten.",
  ] },
  { id: "mangel", icon: AlertTriangle, title: "Mangel oder Ticket anlegen", steps: [
    "Bauplan öffnen und auf die Stelle tippen, um eine Position zu setzen, oder eine vorhandene Position anklicken.",
    "Im Positionsfenster „Mangel dokumentieren“ bzw. „Ticket / Mangel / Aufgabe“ antippen.",
    "Bezeichnung, Priorität und Frist eintragen, im Feld „Zuständig“ den Mitarbeiter wählen und Fotos anhängen.",
    "Speichern – der Zuständige sieht den Eintrag sofort unter „Meine Aufgaben“.",
  ] },
  { id: "ticket", icon: Ticket, title: "Neues Ticket ohne Bauplan anlegen", steps: [
    "Unter „Tickets“ auf „Neues Ticket erstellen“ tippen.",
    "Projekt wählen, dann Bauplan und Position – oder „Ohne Position“.",
    "Optional den Marker direkt im Bauplan setzen.",
    "Nach dem Speichern direkt Fotos aufnehmen oder hochladen.",
  ] },
  { id: "hinweise", icon: Info, title: "Hinweise an Positionen", steps: [
    "Im Positionsfenster den Reiter „Hinweise“ öffnen.",
    "Stufe Info, Achtung oder Warnung wählen, Titel und Beschreibung eintragen und Fotos anhängen.",
    "Hinweise erscheinen in jedem Bericht direkt bei ihrer Position.",
  ] },
  { id: "marker", icon: Move, title: "Marker verschieben & Schildgröße", steps: [
    "Im Bauplan den Schalter „Verschieben“ auf „zugelassen“ stellen (Standard: blockiert, gilt nur für Ihre Ansicht).",
    "Marker per Ziehen versetzen – „Rückgängig“ nimmt bis zu 20 Schritte zurück.",
    "Der Regler „Alle Schilder“ (ab 30 %) stellt alle Schilder des Plans gleich groß ein; die Größe wird pro Bauplan gemerkt.",
    "Liegen Marker übereinander, zeigt ein Tipp eine Auswahlliste aller Positionen an dieser Stelle.",
  ] },
  { id: "zuweisen", icon: ListChecks, title: "Aufgaben zuweisen", steps: [
    "Im Positionsfenster beim Mangel oder Ticket auf das Stift-Symbol (Bearbeiten) tippen.",
    "Im Feld „Zuständig“ den Mitarbeiter auswählen und speichern.",
    "Leitung und Admin sehen unter „Meine Aufgaben“ › „Teamaufgaben“ alle Aufgaben ihrer Mitarbeiter.",
    "Im Bauplan zeigt der Filter „Zuständig“ nur die Positionen eines Mitarbeiters.",
  ] },
  { id: "loeschanfrage", icon: Trash2, title: "Löschanfrage stellen (Haustechnik)", steps: [
    "Mangel beheben und im Positionsfenster ein neues Foto nach der Behebung hochladen.",
    "Beim Mangel „Löschung anfragen“ antippen und optional einen Hinweis eintragen.",
    "Die Anfrage geht an Administrator, Geschäftsleitung und Leitung – bis zur Entscheidung bleibt der Mangel sichtbar.",
  ] },
  { id: "bestaetigen", icon: Trash2, title: "Löschanfragen bestätigen (Admin, Geschäftsleitung & Leitung)", steps: [
    "Menüpunkt „Anfragen“ öffnen – die Zahl am Menüpunkt zeigt die offenen Anfragen.",
    "Vorher- und Nachher-Foto vergleichen.",
    "„Bestätigen & archivieren“ verschiebt den Mangel zu „Erledigte Mängel“. Nach 6 Wochen wird er automatisch gelöscht.",
    "Bei „Ablehnen“ optional eine Begründung angeben – der Mangel bleibt offen.",
  ] },
  { id: "position", icon: MapPin, title: "Positionsfenster: Mengen, Fotos & Verlauf", steps: [
    "Im Reiter „Mengen“ Aufmaße zur Position erfassen.",
    "Im Reiter „Fotos“ Bilder und Dokumente hochladen – per Klick werden sie groß angezeigt.",
    "Im Reiter „Verlauf“ Kommentare schreiben und alle Änderungen nachverfolgen.",
    "Die Schildgröße der Position lässt sich oben im Positionsfenster zwischen 30 und 120 % einstellen.",
  ] },
  { id: "pruefungen", icon: ClipboardCheck, title: "Prüfungen & Wartungen", steps: [
    "Unter „Prüfungen“ alle fälligen und überfälligen Prüfungen ansehen.",
    "Prüfung öffnen, Ergebnis und Datum eintragen und Nachweise anhängen.",
    "Die nächste Fälligkeit wird bei wiederkehrenden Prüfungen automatisch berechnet.",
  ] },
  { id: "berichte", icon: FileStack, title: "Berichte erstellen", steps: [
    "Im Projekt „Dokumentation erstellen“ oder unter „Mehr“ › „Berichte“ den gewünschten Bericht wählen.",
    "Unter „Mängel“ gibt es den Mängelbericht (PDF) und den kompakten Mängelbericht TEXT.",
    "Im Mängelbericht bleibt der Bauplan-Ausschnitt immer auf der ersten Seite; Fotos stehen bündig nebeneinander (Hochformat 3, Querformat 2). Vorher/Nachher erscheint erst bei erledigten Mängeln.",
    "Bei vielen Fotos läuft die Erstellung im Hintergrund – der Bericht erscheint danach zum Download.",
  ] },
  { id: "fotobericht", icon: Printer, title: "Fotobericht drucken", steps: [
    "Unter „Fotos“ auf „Fotobericht“ tippen und die gewünschten Fotos anhaken (oder „Alle“).",
    "Optional „Mit Beschriftung“ (Datum, Position, Beschreibung) und „Mit Deckblatt“ einschalten.",
    "„Bericht drucken“ – Querformat-Fotos kommen zu zweit, Hochformat-Fotos zu viert auf eine DIN-A4-Seite.",
    "In der Großansicht blättern Sie mit den Pfeilen oder den Pfeiltasten.",
  ] },
  { id: "offline", icon: CloudDownload, title: "Offline arbeiten", steps: [
    "Nach dem Login werden alle Daten und Fotos automatisch für die Offline-Nutzung geladen.",
    "Die Projektkarte zeigt „Offline verfügbar“ und wie viele Mängel, Tickets und Fotos gespeichert sind, z. B. „12 von 14 Fotos“.",
    "Fehlen Fotos, auf die bernsteinfarbene Zahl tippen und „Alle nachladen“ oder einzelne Fotos laden. „Offline nachladen“ lädt ein ganzes Projekt oder einen Bauplan neu.",
    "Ohne Internet weist die Projektseite darauf hin, wenn Fotos fehlen. Offline erfasste Einträge und Fotos werden übertragen, sobald wieder Netz da ist.",
  ] },
  { id: "menue", icon: SlidersHorizontal, title: "Menü einstellen", steps: [
    "Im Profilmenü „Menü einstellen“ öffnen.",
    "Für jeden Punkt wählen, ob er direkt in der Leiste oder unter „Mehr“ steht, und die Reihenfolge mit den Pfeilen ändern.",
    "„Standard“ stellt die ursprüngliche Aufteilung wieder her.",
  ] },
  { id: "zugang", icon: UserCheck, title: "Zugangsanfragen freigeben (Plattform-Admin)", steps: [
    "Neue Google-Anmeldungen erzeugen eine Zugangsanfrage – der Admin erhält eine E-Mail.",
    "Im Profilmenü „Zugangsanfragen“ öffnen oder den Link in der E-Mail nutzen.",
    "Bei der Freigabe wählen: eigene Firma oder Beitritt zu einer bestehenden Firma mit Rolle.",
  ] },
];

export default function Help() {
  const { startTour } = useTour();
  const [params] = useSearchParams();
  const thema = TOPICS.some((t) => t.id === params.get("thema")) ? params.get("thema") : undefined;
  useEffect(() => {
    if (thema) { document.querySelector(`[data-testid="help-topic-${thema}"]`)?.scrollIntoView({ block: "center" }); }
  }, [thema]);
  return (
    <div className="space-y-5 max-w-3xl" data-testid="help-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-head font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><LifeBuoy className="h-8 w-8 text-primary" />Hilfe</h1>
        <p className="text-slate-500 mt-1">Die wichtigsten Abläufe Schritt für Schritt.</p>
      </div>
      <Accordion key={thema} type="single" collapsible defaultValue={thema} className="bg-white border border-slate-200 rounded-xl px-4" data-testid="help-accordion">
        {TOPICS.map((t) => (
          <AccordionItem key={t.id} value={t.id} data-testid={`help-topic-${t.id}`}>
            <AccordionTrigger className="text-left" data-testid={`help-trigger-${t.id}`}>
              <span className="flex items-center gap-2 font-semibold text-slate-900"><t.icon className="h-4 w-4 text-primary shrink-0" />{t.title}</span>
            </AccordionTrigger>
            <AccordionContent>
              <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-600">{t.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <div className="flex flex-wrap gap-3 text-sm">
        <button onClick={startTour} className="inline-flex items-center gap-1 text-primary font-medium" data-testid="help-start-tour"><Compass className="h-4 w-4" />Rundgang starten</button>
        <Link to="/aufgaben" className="inline-flex items-center gap-1 text-primary font-medium" data-testid="help-open-tasks"><ListChecks className="h-4 w-4" />Zu Meine Aufgaben</Link>
      </div>
    </div>
  );
}
